/**
 * currencyContext — the org's base currency, fetched once.
 *
 * Per-document screens read the currency stamped on the document; that is
 * always the right answer there. But aggregate screens — dashboard, journals,
 * statements, ledgers — total across many documents, so no single document
 * answers the question. They render in the currency the ORG books in, which is
 * `org_billing_profiles.base_currency`.
 *
 * It lives in a context because seven screens need it and each fetching the
 * profile separately would be seven calls for one immutable fact.
 *
 * Note what this deliberately does NOT do: it never converts. A total that
 * mixes currencies is a reporting question the server answers, not something a
 * formatter should paper over by relabelling the symbol.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getOrgProfile } from '../services/billing/accountant.service';
import { DEFAULT_CURRENCY } from '../utils/format';

interface CurrencyValue {
  /** The currency the org books in. DEFAULT_CURRENCY until the profile loads. */
  baseCurrency: string;
  /** False while the profile is in flight, so a screen can avoid a flash of the wrong symbol. */
  ready: boolean;
}

const Ctx = createContext<CurrencyValue>({ baseCurrency: DEFAULT_CURRENCY, ready: false });

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [baseCurrency, setBaseCurrency] = useState(DEFAULT_CURRENCY);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    getOrgProfile()
      .then((p) => {
        if (!alive) return;
        if (p?.base_currency) setBaseCurrency(p.base_currency);
        setReady(true);
      })
      // A failed profile fetch leaves the default in place rather than blanking
      // every amount in the app — the screens still render, just in the default.
      .catch(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  const value = useMemo(() => ({ baseCurrency, ready }), [baseCurrency, ready]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useBaseCurrency(): CurrencyValue {
  return useContext(Ctx);
}
